import { ReactNode, RefObject, useImperativeHandle } from 'react';
import { DefaultValues, FieldValues, FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import { Flex } from '@servicetitan/anvil2';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ValidationSummary } from '@servicetitan/financing-utils-shared';

export interface ImperativeForm {
    submit: () => Promise<void>;
    isValid: () => boolean;
}

export interface FormProps<T extends FieldValues> {
    formRef: RefObject<ImperativeForm>;
    onSubmit?: (data: Partial<T>) => void;
    formData: DefaultValues<T>;
    schema: z.ZodType<T>;
    children: ReactNode | ((methods: UseFormReturn<T>) => ReactNode);
}

export const Form = <T extends FieldValues>({
    onSubmit = () => {},
    formRef,
    children,
    formData,
    schema,
}: FormProps<T>) => {
    const methods = useForm<T>({
        mode: 'onBlur',
        defaultValues: formData,
        shouldUnregister: true,
        criteriaMode: 'all',
        resolver: zodResolver(schema),
    });

    // Allow external components to submit the form
    useImperativeHandle(formRef, () => ({
        async submit() {
            return methods.handleSubmit(async data => {
                await methods.trigger();
                onSubmit(data);
            })();
        },
        isValid() {
            // Check both formState.isValid and ensure no validation errors exist
            const hasErrors = Object.keys(methods.formState.errors).length > 0;
            return methods.formState.isValid && !hasErrors;
        },
    }));

    return (
        <FormProvider {...methods}>
            <form onSubmit={e => e.preventDefault()} noValidate>
                <Flex direction="column" gap="8">
                    <ValidationSummary />
                    <Flex direction="column" gap="4">
                        {typeof children === 'function' ? children(methods) : children}
                    </Flex>
                </Flex>
            </form>
        </FormProvider>
    );
};
